import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
  ComposedChart, Area, AreaChart, ReferenceLine, ReferenceArea
} from 'recharts';
import EarningsDashboard from './components/EarningsDashboard';

const API_BASE = 'https://etoro-api-v2.skumar1988.workers.dev/api';

// ── Auth helpers ──
function getAuthToken() {
  return localStorage.getItem('etoro_token') || '';
}
function setAuthToken(token) {
  localStorage.setItem('etoro_token', token);
}
function clearAuthToken() {
  localStorage.removeItem('etoro_token');
}
function isAuthenticated() {
  const token = getAuthToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp > Date.now() / 1000;
  } catch {
    return false;
  }
}

async function authedFetch(url, options = {}) {
  const token = getAuthToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = 'Bearer ' + token;
  const resp = await fetch(url, { ...options, headers });
  if (resp.status === 401) {
    clearAuthToken();
    window.location.reload();
    return;
  }
  return resp;
}

async function doLogin(username, password) {
  const resp = await fetch(API_BASE + '/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  return resp.json();
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#a4de6c'];
const REFRESH_INTERVAL = 30000;

// ── API Hook ───────────────────────────────────────────────
function useApi(endpoint, options = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timestamp, setTimestamp] = useState(null);
  const body = useMemo(() => options.body || { environment: 'real' }, [options.body?.environment]);
  const interval = options.interval || 0;

  const fetchData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);
      const response = await authedFetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (result.error) throw new Error(result.error);
      setData(result);
      setTimestamp(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [endpoint, body]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!interval) return;
    const id = setInterval(() => fetchData(false), interval);
    return () => clearInterval(id);
  }, [interval, fetchData]);

  return { data, loading, error, refetch: () => fetchData(true), timestamp };
}

// ── Formatters ─────────────────────────────────────────────
const fmtCurr = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v);
const fmtNum = (v) => v == null ? '0.00' : new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
const fmtPct = (v) => v == null ? '0.00%' : new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: 2 }).format(v / 100);
const fmtTime = (d) => d ? new Date(d).toLocaleString() : '';

// ── Instrument Name Cache ──────────────────────────────────
function useInstrumentNameCache() {
  const { data } = useApi('/instruments', { body: { environment: 'real' } });
  return useMemo(() => {
    const map = {};
    (data?.instruments || []).forEach(inst => {
      map[inst.instrumentID] = inst.symbolFull || inst.instrumentDisplayName || `ID ${inst.instrumentID}`;
    });
    return map;
  }, [data]);
}

// ── Overview Cards ─────────────────────────────────────────
function StatCard({ title, value, positive, subtitle, icon, color }) {
  const isPos = positive === true || (positive === undefined && value >= 0);
  const isNeg = positive === false || (positive === undefined && value < 0);
  return (
    <div className="stat-card" style={{ borderTop: `3px solid ${color || '#0052cc'}` }}>
      <div className="stat-header">
        <span className="stat-icon">{icon}</span>
        <span className="stat-title">{title}</span>
      </div>
      <div className={`stat-value ${isPos ? 'positive' : ''} ${isNeg ? 'negative' : ''}`}>{value}</div>
      {subtitle && <div className="stat-subtitle">{subtitle}</div>}
    </div>
  );
}

// ── Loading Shimmer ────────────────────────────────────────
function LoadingShimmer({ count = 4 }) {
  return (
    <div className="shimmer-grid">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="shimmer-card">
          <div className="shimmer-line" />
          <div className="shimmer-line short" />
        </div>
      ))}
    </div>
  );
}

// ── Charts ─────────────────────────────────────────────────
function AllocationChart({ positions, instrumentNames }) {
  if (!positions?.length) return <div className="chart-placeholder">No positions</div>;
  const data = positions
    .filter(p => p.amount > 0)
    .map(p => ({
      name: instrumentNames[p.instrumentID] || `ID ${p.instrumentID}`,
      value: p.amount,
      color: COLORS[positions.indexOf(p) % COLORS.length],
    }));
  return (
    <div className="chart-wrapper">
      <h3>Allocation</h3>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`} labelLine={false}>
            {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function PnLPie({ positions }) {
  if (!positions?.length) return <div className="chart-placeholder">No P&L data</div>;
  const winners = positions.filter(p => (p.unrealizedPnL?.pnL || 0) > 0).length;
  const losers = positions.filter(p => (p.unrealizedPnL?.pnL || 0) < 0).length;
  const flat = positions.filter(p => (p.unrealizedPnL?.pnL || 0) === 0).length;
  if (winners + losers + flat === 0) return <div className="chart-placeholder">No P&L data</div>;
  const data = [
    { name: 'Winners', value: winners, color: '#00C49F' },
    { name: 'Losers', value: losers, color: '#FF4D4F' },
    { name: 'Flat', value: flat, color: '#8884d8' },
  ].filter(d => d.value > 0);
  return (
    <div className="chart-wrapper">
      <h3>Win / Loss</h3>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`} labelLine={false}>
            {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Positions Table ────────────────────────────────────────
function PositionsTable({ positions, loading, error, onClose, instrumentNames }) {
  if (loading) return <LoadingShimmer count={5} />;
  if (error) return <div className="error-banner">Failed to load positions: {error}</div>;
  if (!positions?.length) return <div className="empty-state">No open positions</div>;
  return (
    <div className="table-wrapper">
      <table className="positions-table">
        <thead>
          <tr>
            <th>Instrument</th>
            <th>Side</th>
            <th>Amount</th>
            <th>Open Rate</th>
            <th>Current Rate</th>
            <th>P&L</th>
            <th>SL / TP</th>
            <th>Leverage</th>
            <th>Opened</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {positions.map(pos => {
            const name = instrumentNames[pos.instrumentID] || `ID ${pos.instrumentID}`;
            const upnl = pos.unrealizedPnL || {};
            const pnl = upnl.pnL || 0;
            const closeRate = upnl.closeRate || 0;
            return (
              <tr key={pos.positionID} className={pnl > 0 ? 'winner' : pnl < 0 ? 'loser' : ''}>
                <td className="instrument-cell">
                  <span className="instrument-name">{name}</span>
                  <span className="instrument-id">#{pos.instrumentID}</span>
                </td>
                <td><span className={`side-badge ${pos.isBuy ? 'buy' : 'sell'}`}>{pos.isBuy ? 'Buy' : 'Sell'}</span></td>
                <td>{fmtCurr(pos.amount)}</td>
                <td>{fmtNum(pos.openRate)}</td>
                <td>{closeRate ? fmtNum(closeRate) : '—'}</td>
                <td className={`pnl-cell ${pnl > 0 ? 'positive' : pnl < 0 ? 'negative' : ''}`}>
                  {pnl >= 0 ? '+' : ''}{fmtCurr(pnl)}
                </td>
                <td className="sl-tp-cell">
                  {pos.stopLossRate ? fmtNum(pos.stopLossRate) : '—'} / {pos.takeProfitRate ? fmtNum(pos.takeProfitRate) : '—'}
                </td>
                <td>x{pos.leverage || 1}</td>
                <td className="date-cell">{pos.openDateTime ? new Date(pos.openDateTime).toLocaleDateString() : '—'}</td>
                <td><button className="btn btn-icon btn-danger" onClick={() => onClose(pos)} title="Close">✕</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Trade Panel ────────────────────────────────────────────
function TradePanel({ prefilled, onSuccess, onCancel, instrumentNames }) {
  const [instrumentId, setInstrumentId] = useState(prefilled?.instrumentId || '');
  const [instrumentName, setInstrumentName] = useState(prefilled?.instrumentName || '');
  const [amount, setAmount] = useState('');
  const [side, setSide] = useState('Buy');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!instrumentId || !amount) return;
    setSubmitting(true);
    setMessage({ text: '', type: '' });
    try {
      const resp = await authedFetch(`${API_BASE}/trade/place`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instrumentID: parseInt(instrumentId),
          amount: parseFloat(amount),
          side,
          stopLossRate: stopLoss ? parseFloat(stopLoss) : undefined,
          takeProfitRate: takeProfit ? parseFloat(takeProfit) : undefined,
          environment: 'real',
        }),
      });
      const result = await resp.json();
      if (result.error) throw new Error(result.error);
      setMessage({ text: 'Order placed successfully!', type: 'success' });
      setTimeout(() => { onSuccess(); onCancel(); }, 1500);
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="trade-panel">
      <div className="section-header">
        <h2>Place Trade</h2>
        <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>Instrument</label>
            <div className="instrument-select-wrapper">
              <select className="input" value={instrumentId} onChange={e => {
                setInstrumentId(e.target.value);
                const opt = e.target.options[e.target.selectedIndex];
                setInstrumentName(opt.text || '');
              }}>
                <option value="">Select...</option>
                {Object.entries(instrumentNames).map(([id, name]) => (
                  <option key={id} value={id}>{name} (ID {id})</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Amount ($)</label>
            <input type="number" className="input" step="0.01" min="10" value={amount} onChange={e => setAmount(e.target.value)} placeholder="100" required />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Side</label>
            <div className="radio-group">
              <label><input type="radio" name="side" value="Buy" checked={side === 'Buy'} onChange={e => setSide(e.target.value)} /> Buy</label>
              <label><input type="radio" name="side" value="Sell" checked={side === 'Sell'} onChange={e => setSide(e.target.value)} /> Sell</label>
            </div>
          </div>
          <div className="form-group">
            <label>Stop Loss (%)</label>
            <input type="number" className="input" step="0.01" min="0" max="100" value={stopLoss} onChange={e => setStopLoss(e.target.value)} placeholder="Optional" />
          </div>
          <div className="form-group">
            <label>Take Profit (%)</label>
            <input type="number" className="input" step="0.01" min="0" value={takeProfit} onChange={e => setTakeProfit(e.target.value)} placeholder="Optional" />
          </div>
        </div>
        {message.text && <div className={`form-message ${message.type}`}>{message.text}</div>}
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Placing...' : 'Place Order'}
        </button>
      </form>
    </div>
  );
}

// ── Trade Signals ──────────────────────────────────────────
function TradeSignals({ onSelectInstrument }) {
  const { data, loading, error, refetch } = useApi('/ai-analysis', { body: { environment: 'real' } });
  if (loading) return <LoadingShimmer count={3} />;
  if (error) return <div className="error-banner">Failed to load signals: {error}</div>;
  const summary = data?.summary || {};
  const byInstrument = data?.byInstrument || [];
  return (
    <div className="signals-panel">
      <div className="section-header">
        <h2>Trade Signals</h2>
        <button className="btn btn-secondary btn-sm" onClick={refetch}>Refresh</button>
      </div>
      <div className="signals-summary">
        <StatCard title="Total Invested" value={fmtCurr(summary.totalInvested)} color="#0052cc" icon="💰" />
        <StatCard title="Total Return" value={fmtCurr(summary.totalReturn)} color={summary.totalReturn >= 0 ? '#00C49F' : '#FF4D4F'} icon="📈" positive={summary.totalReturn >= 0} subtitle={`${fmtPct(summary.returnPct)}`} />
        <StatCard title="Positions" value={summary.positionCount} color="#13c2c2" icon="📋" />
      </div>
      <div className="table-wrapper">
        <table className="positions-table">
          <thead>
            <tr>
              <th>Instrument</th>
              <th>Amount</th>
              <th>P&L</th>
              <th>Positions</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {byInstrument.map(item => (
              <tr key={item.instrumentID}>
                <td className="instrument-cell">
                  <span className="instrument-name">{instrumentNames[item.instrumentID] || `ID ${item.instrumentID}`}</span>
                </td>
                <td>{fmtCurr(item.amount)}</td>
                <td className={`pnl-cell ${item.pnl > 0 ? 'positive' : item.pnl < 0 ? 'negative' : ''}`}>
                  {item.pnl >= 0 ? '+' : ''}{fmtCurr(item.pnl)}
                </td>
                <td>{item.positions}</td>
                <td>
                  <button className="btn btn-secondary btn-sm" onClick={() => onSelectInstrument({ instrumentID: item.instrumentID, instrumentDisplayName: instrumentNames[item.instrumentID] })}>Trade</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Watchlists ─────────────────────────────────────────────
function Watchlists({ watchlists, onSelectInstrument, instrumentNames }) {
  if (!watchlists?.length) return <div className="empty-state">No watchlists</div>;
  return (
    <div className="watchlists-panel">
      <div className="section-header"><h2>Watchlists</h2></div>
      {watchlists.map(wl => (
        <div key={wl.watchlistID} className="watchlist-section">
          <h3>{wl.name} ({wl.itemCount} items)</h3>
          <div className="table-wrapper">
            <table className="positions-table">
              <thead>
                <tr><th>Instrument</th><th>Last Price</th><th>Change</th><th>Action</th></tr>
              </thead>
              <tbody>
                {(wl.items || []).map(item => (
                  <tr key={item.itemId || item.instrumentID}>
                    <td className="instrument-cell">
                      <span className="instrument-name">{instrumentNames[item.instrumentID] || item.market?.displayName || `ID ${item.instrumentID}`}</span>
                    </td>
                    <td>{item.lastPrice ? fmtNum(item.lastPrice) : '—'}</td>
                    <td className={item.changePercent >= 0 ? 'positive' : 'negative'}>
                      {item.changePercent != null ? (item.changePercent >= 0 ? '+' : '') + fmtPct(item.changePercent) : '—'}
                    </td>
                    <td>
                      <button className="btn btn-secondary btn-sm" onClick={() => onSelectInstrument(item)}>Trade</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Prompts / History / Charts placeholders ────────────────
function Placeholder({ title, icon }) {
  return (
    <div className="placeholder-panel">
      <div className="section-header"><h2>{icon} {title}</h2></div>
      <div className="empty-state">Coming soon</div>
    </div>
  );
}

// ── Modals ─────────────────────────────────────────────────
function SettingsModal({ open, onClose, environment, onEnvironmentChange }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Settings</h3>
          <button className="btn btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Environment</label>
            <select className="input" value={environment} onChange={e => onEnvironmentChange(e.target.value)}>
              <option value="real">Real</option>
              <option value="demo">Demo</option>
              <option value="auto">Auto</option>
            </select>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function CloseConfirmModal({ position, onConfirm, onCancel, submitting }) {
  if (!position) return null;
  const name = position.instrumentDisplayName || `ID ${position.instrumentID}`;
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>Close Position</h3></div>
        <div className="modal-body">
          <p>Close <strong>{name}</strong>?</p>
          <p className="text-muted">Amount: {fmtCurr(position.amount)} | P&L: {position.unrealizedPnL?.pnL != null ? (position.unrealizedPnL.pnl >= 0 ? '+' : '') + fmtCurr(position.unrealizedPnL.pnl) : '—'}</p>
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={submitting}>{submitting ? 'Closing...' : 'Close Position'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Main App ───────────────────────────────────────────────
function App() {
  const [activeTab, setActiveTab] = useState('overview');
  const [environment, setEnvironment] = useState('real');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [closeTarget, setCloseTarget] = useState(null);
  const [closingPosition, setClosingPosition] = useState(false);
  const [toast, setToast] = useState(null);
  const [prefilledTrade, setPrefilledTrade] = useState(null);
  const [authenticated, setAuthenticated] = useState(isAuthenticated());
  const [loginUsername, setLoginUsername] = useState('admin');
  const [loginPassword, setLoginPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const instrumentNames = useInstrumentNameCache();
  const tradeRefreshRef = useRef(null);

  const { data: portfolioData, loading: portfolioLoading, error: portfolioError, refetch: refetchPortfolio, timestamp: ts } = useApi('/portfolio', { body: { environment }, interval: REFRESH_INTERVAL });
  const { data: positionsData, loading: positionsLoading, error: positionsError, refetch: refetchPositions } = useApi('/positions', { body: { environment }, interval: REFRESH_INTERVAL });
  const { data: pnlData, loading: pnlLoading } = useApi('/pnl', { body: { environment }, interval: REFRESH_INTERVAL });
  const { data: watchlistsData, loading: watchlistsLoading } = useApi('/watchlists', { body: { environment }, interval: 60000 });

  const positions = positionsData?.positions || portfolioData?.positions || [];
  const watchlists = watchlistsData?.watchlists || [];
  const totalEquity = portfolioData?.totalEquity ?? pnlData?.credit ?? 0;
  const totalInvested = portfolioData?.totalInvested ?? 0;
  const totalReturn = portfolioData?.totalReturn ?? 0;

  const winCount = useMemo(() => positions.filter(p => (p.unrealizedPnL?.pnL || 0) > 0).length, [positions]);
  const winRate = positions.length > 0 ? ((winCount / positions.length) * 100) : 0;

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleCloseClick = (pos) => setCloseTarget(pos);

  const handleCloseConfirm = async () => {
    if (!closeTarget) return;
    setClosingPosition(true);
    try {
      const resp = await authedFetch(`${API_BASE}/trade/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          positionId: closeTarget.positionID,
          instrumentId: closeTarget.instrumentID,
          environment: 'real',
        }),
      });
      const result = await resp.json();
      if (result.error) {
        showToast(`Close failed: ${result.error}`, 'error');
      } else {
        showToast('Position closed successfully');
        refetchPortfolio();
        refetchPositions();
      }
    } catch (e) {
      showToast(`Error: ${e.message}`, 'error');
    } finally {
      setClosingPosition(false);
      setCloseTarget(null);
    }
  };

  const handleTradeSuccess = () => {
    showToast('Order placed successfully!');
    setTimeout(() => {
      refetchPortfolio();
      refetchPositions();
    }, 2000);
  };

  const handleSelectWatchlistInstrument = (item) => {
    const instrumentId = item.instrumentID || item.instrumentId || item.itemId;
    if (instrumentId) {
      const name = item.market?.displayName || item.instrumentDisplayName || `ID ${instrumentId}`;
      setPrefilledTrade({ instrumentId: String(instrumentId), instrumentName: name });
      setActiveTab('trade');
    }
  };

  const tabs = [
    { id: 'overview', icon: '📊', label: 'Overview' },
    { id: 'positions', icon: '📋', label: 'Positions' },
    { id: 'trade', icon: '🛒', label: 'Trade' },
    { id: 'signals', icon: '🎯', label: 'Trade Signals' },
    { id: 'history', icon: '📜', label: 'History' },
    { id: 'watchlists', icon: '⭐', label: 'Watchlists' },
    { id: 'prompts', icon: '📋', label: 'Prompts' },
    { id: 'charts', icon: '📈', label: 'Charts' },
    { id: 'earnings', icon: '💰', label: 'Earnings' },
  ];

  // ── Login Handler ──
  async function handleLogin() {
    setLoggingIn(true);
    setLoginError(null);
    try {
      const result = await doLogin(loginUsername, loginPassword);
      if (result.token) {
        setAuthToken(result.token);
        setAuthenticated(true);
        setLoginPassword('');
      } else {
        setLoginError(result.error || 'Login failed');
      }
    } catch (e) {
      setLoginError('Connection error');
    } finally {
      setLoggingIn(false);
    }
  }

  return (
    <>
      {/* ── Login Screen ── */}
      {!authenticated && (
        <div className="login-screen">
          <div className="login-card">
            <h2>eToro Dashboard</h2>
            <p className="login-subtitle">Sign in to access your portfolio</p>
            {loginError && <div className="login-error">{loginError}</div>}
            <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={loginUsername}
                  onChange={(e) => setLoginUsername(e.target.value)}
                  placeholder="admin"
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="••••••"
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary btn-block" disabled={loggingIn}>
                {loggingIn ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── Main App ── */}
      {authenticated && (
        <div className="app">
          {/* Header */}
          <header className="header">
            <div className="header-left">
              <h1>eToro Dashboard</h1>
              <span className={`env-badge ${environment}`}>{environment.toUpperCase()}</span>
            </div>
            <div className="header-right">
              {ts && <span className="refresh-time">Updated {fmtTime(ts)}</span>}
              <button className="btn btn-icon" onClick={() => { refetchPortfolio(); showToast('Refreshing...'); }} title="Refresh">🔄</button>
              <button className="btn btn-icon" onClick={() => setSettingsOpen(true)} title="Settings">⚙️</button>
            </div>
          </header>

          {/* Toast */}
          {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}

          {/* Tab Bar */}
          <TabBar tabs={tabs} active={activeTab} onChange={setActiveTab} />

          {/* Content */}
          <main className="main">
            {/* ── OVERVIEW ── */}
            {activeTab === 'overview' && (
              <>
                {portfolioLoading ? (
                  <LoadingShimmer count={4} />
                ) : portfolioError ? (
                  <div className="error-banner">Failed to load portfolio: {portfolioError}</div>
                ) : (
                  <div className="overview-grid">
                    <StatCard title="Total Equity" value={fmtCurr(totalEquity)} color="#0052cc" icon="💰" positive={totalEquity >= totalInvested} />
                    <StatCard title="Total Invested" value={fmtCurr(totalInvested)} color="#722ed1" icon="📈" />
                    <StatCard title="Total Return" value={fmtCurr(totalReturn)} color={totalReturn >= 0 ? '#00C49F' : '#FF4D4F'} icon="📉" positive={totalReturn >= 0}
                      subtitle={`${fmtPct(totalInvested > 0 ? (totalReturn / totalInvested) * 100 : 0)} return`} />
                    <StatCard title="Open Positions" value={positions.length} color="#13c2c2" icon="📋" />
                  </div>
                )}

                <div className="stats-secondary">
                  <StatCard title="Winners" value={`${winCount} / ${positions.length}`} color="#00C49F" icon="✅" positive={true}
                    subtitle={`${winRate.toFixed(1)}% win rate`} />
                  <StatCard title="Unrealized P&L" value={fmtCurr(pnlData?.pnl?.unrealizedPnL ?? 0)} color="#fa8c16" icon="💵"
                    positive={(pnlData?.pnl?.unrealizedPnL ?? 0) >= 0} />
                  <StatCard title="Credit" value={fmtCurr(pnlData?.pnl?.credit ?? 0)} color="#722ed1" icon="🏦" positive={true} />
                </div>

                <div className="charts-row">
                  <AllocationChart positions={positions} instrumentNames={instrumentNames} />
                  <PnLPie positions={positions} />
                </div>
              </>
            )}

            {/* ── POSITIONS ── */}
            {activeTab === 'positions' && (
              <>
                <div className="section-header">
                  <h2>Open Positions ({positions.length})</h2>
                  <button className="btn btn-secondary btn-sm" onClick={() => refetchPositions()}>Refresh</button>
                </div>
                <PositionsTable
                  positions={positions}
                  loading={positionsLoading}
                  error={positionsError}
                  onClose={handleCloseClick}
                  instrumentNames={instrumentNames}
                />
              </>
            )}

            {/* ── TRADE ── */}
            {activeTab === 'trade' && (
              <TradePanel
                prefilled={prefilledTrade}
                onSuccess={handleTradeSuccess}
                onCancel={() => setPrefilledTrade(null)}
                instrumentNames={instrumentNames}
              />
            )}

            {/* ── SIGNALS ── */}
            {activeTab === 'signals' && (
              <TradeSignals
                onSelectInstrument={handleSelectWatchlistInstrument}
              />
            )}

            {/* ── HISTORY ── */}
            {activeTab === 'history' && <Placeholder title="Trade History" icon="📜" />}

            {/* ── WATCHLISTS ── */}
            {activeTab === 'watchlists' && (
              <Watchlists
                watchlists={watchlists}
                onSelectInstrument={handleSelectWatchlistInstrument}
                instrumentNames={instrumentNames}
              />
            )}

            {/* ── PROMPTS ── */}
            {activeTab === 'prompts' && <Placeholder title="Prompts" icon="📋" />}

            {/* ── CHARTS ── */}
            {activeTab === 'charts' && <Placeholder title="Charts" icon="📈" />}

            {/* ── EARNINGS ── */}
            {activeTab === 'earnings' && <EarningsDashboard />}

          </main>

          {/* Modals */}
          <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} environment={environment} onEnvironmentChange={setEnvironment} />
          <CloseConfirmModal position={closeTarget} onConfirm={handleCloseConfirm} onCancel={() => setCloseTarget(null)} submitting={closingPosition} />
        </div>
      )}
    </>
  );
}

// ── TabBar Component ─────────────────────────────────────────
function TabBar({ tabs, active, onChange }) {
  return (
    <div className="tab-bar" role="tablist">
      {tabs.map(tab => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={active === tab.id}
          className={`tab ${active === tab.id ? 'active' : ''}`}
          onClick={() => onChange(tab.id)}
        >
          <span className="tab-icon">{tab.icon}</span>
          <span className="tab-label">{tab.label}</span>
        </button>
      ))}
    </div>
  );
}

export default App;